import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import type { Values } from "./decision-tree.models";
import {
  buildRenderedTree,
  RenderedTree,
  TreeCounts,
} from "./rendered-decision-tree.layout";

@Component({
  selector: "app-rendered-decision-tree",
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: "./rendered-decision-tree.component.html",
  styleUrls: ["./rendered-decision-tree.component.scss"],
})
export class RenderedDecisionTreeComponent implements OnChanges {
  private static nextId = 0;
  @Input() hasSellout = false;
  @Input() hasVilcRef = true;
  @Input() isYtd = false;
  @Input() animate = true;
  @Input() skuRecommendationVersion = "V3";
  @Input() values: Partial<Values> = {};
  @Input() counts: TreeCounts = {};
  @Input() assetBasePath = "assets/images/decision-tree-icons";
  readonly markerId = `rendered-tree-arrow-${RenderedDecisionTreeComponent.nextId++}`;
  graph: RenderedTree = buildRenderedTree(false, true, false, {});

  ngOnChanges(): void {
    const values = this.values ?? {};
    const isV2 = this.skuRecommendationVersion.toUpperCase() === "V2";
    const mapped: TreeCounts = {
      nodes: {
        volume: values.totalSKUCount,
        high: values.maintainHighVolumeCount,
        "profit-high": values.highVolumeProfitableCount,
        index: values.vilcRefCount,
        mb: values.isMBCount,
        retention: values.volumeRetentionTestCount,
        assess: values.assessStrategicValueCount,
        low: values.maintainLowVolumeCount,
        focus: values.focusBrandCount,
        "profit-low": values.lowVolumeProfitableCount,
        filter: values.prioritizationFilterCount,
        recovery: values.financialRecoveryTestCount,
      },
      branches: {
        "volume-yes": values.maintainHighVolumeCount,
        "volume-no": values.assessStrategicValueCount,
        "profit-high-yes": values.highVolumeProfitablePassedCount,
        "profit-high-no": values.highVolumeProfitableFailedCount,
        "index-yes": values.vilcRefPassedCount,
        "index-no": values.vilcRefFailedCount,
        "mb-yes": values.isMBPassedCount,
        "mb-no": values.isMBFailedCount,
        "retention-yes": values.volumeRetentionTestPassedCount,
        "retention-no": values.volumeRetentionTestFailedCount,
        "assess-yes": values.assessStrategicValuePassedCount,
        "assess-no": values.assessStrategicValueFailedCount,
        "filter-yes": values.prioritizationFilterPassedCount,
        "filter-no": values.prioritizationFilterFailedCount,
        "focus-yes": values.focusBrandPassedCount,
        "focus-no": values.focusBrandFailedCount,
        "profit-low-yes": values.lowVolumeProfitablePassedCount,
        "profit-low-no": values.lowVolumeProfitableFailedCount,
        "recovery-yes": values.financialRecoveryTestPassedCount,
        "recovery-no": values.financialRecoveryTestFailedCount,
      },
      outcomes: {
        "high-optimize": isV2
          ? values.highVolumeProfitableDtpCount
          : values.highVolumeProfitableOptimizeCount,
        "high-keep": values.highVolumeProfitableKeepCount,
        accelerate: isV2 ? values.growCount : values.accelerateCount,
        "low-keep": values.lowVolumeProfitableKeepCount,
        "low-optimize": isV2
          ? values.lowVolumeProfitableDtpCount
          : values.lowVolumeProfitableOptimizeCount,
        delist: values.delistCount,
      },
    };
    const counts: TreeCounts = {
      nodes: { ...mapped.nodes, ...this.counts?.nodes },
      branches: { ...mapped.branches, ...this.counts?.branches },
      outcomes: { ...mapped.outcomes, ...this.counts?.outcomes },
    };
    this.graph = buildRenderedTree(
      this.hasSellout,
      this.hasVilcRef,
      this.isYtd,
      counts,
      isV2 ? "V2" : "V3",
    );
  }
}
